import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import type { LeaveTypes } from 'modules/timeoff/store/collections/leaveTypes'
import timeoffPolicies from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import type Leave from 'modules/timeoff/store/models/Leave'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

import EmployeeSelector from './EmployeeSelector'
import Types from './Types'

interface Props {
  employee?: Employee | null | undefined
  form: LeaveForm
  leave?: Leave | null | undefined
  leaveTypes: LeaveTypes
  readOnly: boolean
  showEmployeeSelector: boolean
  onSelectEmployee?: (Ids: number[] | (number | null | undefined)) => void
  selectedEmployeeIds?: number[]
  bulkAdd?: boolean
  leaveTypeReadOnly: boolean
  isMedicalLeave: boolean
  selectableEmployees: Employee[]
  hasMedicalLeavesOpen: (employeeId: number) => boolean
  allowancesForPolicy: () => Allowance[]
  selectedLeaveType?: LeaveType
  contractVersions: ContractVersions
}

const CommonComponents = ({
  leaveTypeReadOnly,
  bulkAdd = false,
  leaveTypes,
  form,
  leave,
  readOnly,
  employee,
  showEmployeeSelector,
  onSelectEmployee,
  selectedEmployeeIds,
  isMedicalLeave,
  selectableEmployees,
  hasMedicalLeavesOpen,
  allowancesForPolicy,
  selectedLeaveType,
  contractVersions,
}: Props) => {
  const policyField = form.get('policy_id')
  const { employee: loggedEmployee, canManageTimeoff } = useCurrentSession()
  const options = timeoffPolicies.models.map((policy) => ({
    value: policy.get('id'),
    label: policy.get('name'),
  }))

  const allowanceForPolicyIds = allowancesForPolicy().map((allowance) => allowance.get('id'))

  const leaveTypesForPolicy = leaveTypes.filter(
    (leaveType) =>
      !!leaveType.attributes.active &&
      allowanceForPolicyIds.some((allowanceForPolicyId) =>
        leaveType.get('allowance_ids').includes(allowanceForPolicyId)
      )
  )

  const allLeaveTypeOptions = bulkAdd ? leaveTypesForPolicy : leaveTypes.models

  const employeesManagedByCurrentUser = loggedEmployee?.reports || []

  const isEmployeeManagedByCurrentUser = employeesManagedByCurrentUser.find(
    (report) => report.id == form.get('employee_id').value
  )

  const canRequestRestrictedLeaveType =
    !!isEmployeeManagedByCurrentUser ||
    canManageTimeoff ||
    !!employee?.isTimeoffSupervisedByCurrent()

  const leaveTypeOptions = canRequestRestrictedLeaveType
    ? allLeaveTypeOptions
    : allLeaveTypeOptions.filter((leaveType) => !leaveType.isRestricted)

  // Initialize form field with the leave type in the list when the default value is not available
  if (!leave && leaveTypeOptions.length > 0) {
    const existsLeaveType = leaveTypeOptions.find(
      (leaveType) => leaveType.id == form.get('leave_type_id').value
    )
    if (!existsLeaveType) form.setValues({ leave_type_id: leaveTypeOptions.at(0)!.id })
  }

  const leaveTypeDescription = selectedLeaveType?.attributes.description

  return (
    <>
      {bulkAdd && (
        <FormRow>
          <Select
            label={i18n.t('common.policy')}
            field={policyField}
            options={options}
            showOriginalLabelText
          />
        </FormRow>
      )}
      <Types
        form={form}
        leave={leave}
        leaveTypes={leaveTypeOptions}
        employee={employee}
        readOnly={readOnly || leaveTypeReadOnly}
        hasMedicalLeavesOpen={hasMedicalLeavesOpen}
        leaveTypeDescription={leaveTypeDescription}
      />
      <EmployeeSelector
        form={form}
        showEmployeeSelector={showEmployeeSelector}
        onSelectEmployee={onSelectEmployee}
        selectedEmployeeIds={selectedEmployeeIds}
        selectableEmployees={selectableEmployees}
        isMedicalLeave={isMedicalLeave}
        contractVersions={contractVersions}
      />
    </>
  )
}

export default observer(CommonComponents)
